import React, { HTMLAttributes, useCallback, useMemo, useState } from 'react';
import AccordionPanel from '../AccordionPanel/AccordionPanel';
import AccordionPanelHeader from '../AccordionPanelHeader/AccordionPanelHeader';
import AccordionPanelContent from '../AccordionPanelContent/AccordionPanelContent';
import AccordionContext from './AccordionContext';
import registerDefaults from '../themeDefaults';

registerDefaults();

export interface IAccordionProps extends HTMLAttributes<HTMLDivElement> {
  /** Prop to allow multiple accordian panels to open at one time.
   * If False the current open panel will close when another panel is opened.
   */
  allowMultiplePanelsOpen?: boolean;
}

const Accordion = ({ allowMultiplePanelsOpen = true, children, className }: IAccordionProps) => {
  /**
   * State openPanelIdsArr manages open state of accordion component.
   * @param {array} openPanelIdsArr - Current open panels' IDs array.
   * @param {function} setOpenPanelIdState - Update open Panels' Ids array.
   */
  const [openPanelIdsArr, setOpenPanelIdState] = useState<string[]>([]);

  const handleSetCurrentOpenPanelsIdsArr = useCallback(
    (managedPanelId: string) => {
      setOpenPanelIdState((prevOpenStateArr) => {
        const managedPanelIdIsOpen = prevOpenStateArr.indexOf(managedPanelId) !== -1;

        // If it's not allowed multiple panels to be open and current managed panel is open,
        // no panel header should be open otherwise managed panel id should be open.
        if (!allowMultiplePanelsOpen) {
          return managedPanelIdIsOpen ? [] : [managedPanelId];
        }

        // If current managed panel is already open, so openPanelIdsArr state array
        // contains it's panelId, it's id needed to be removed from state array,
        // otherwise this panelId should be added to openPanelIdsArr state array.
        return managedPanelIdIsOpen
          ? prevOpenStateArr.filter((item) => item !== managedPanelId)
          : prevOpenStateArr.concat([managedPanelId]);
      });
    },
    [allowMultiplePanelsOpen]
  );

  const contextValue = useMemo(
    () => ({ openPanelIdsArr, handleSetCurrentOpenPanelsIdsArr }),
    [openPanelIdsArr, handleSetCurrentOpenPanelsIdsArr]
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={className} data-allow-multiple={allowMultiplePanelsOpen} data-allow-toggle>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

// Bind public components to Accordion so Panel, Header and Content components
// could be used while Accordion has been imported.
Accordion.Panel = AccordionPanel;
Accordion.Header = AccordionPanelHeader;
Accordion.Content = AccordionPanelContent;
Accordion.displayName = 'Accordion';

export default Accordion;
